import { ThemeProvider } from "@material-ui/core/styles";
import React, { Component, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";

import { presentations as presentationsApi } from "apis/callables";
import { MetricName, PresentationActivityType } from "common/interfaces";
import { QuickStartTask } from "common/constants";
import { Icon } from "js/Components/Icon";
import { isDevelopment, isPPTAddin } from "js/config";
import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import QuickStartController from "js/controllers/QuickStartController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { PresentationNotFoundError, PresentationPermissionDeniedError, getPresentation } from "js/core/models/presentation";
import { getExperiments } from "js/core/services/experiments";
import * as geom from "js/core/utilities/geom";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import { EditorPropertyPanel } from "js/editor/ElementEditor/EditorPropertyPanel";
import { app } from "js/namespaces";
import { ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import TaskRendererFactory from "./Components/QuickStart/TaskRendererFactory";
import { AbsoluteBox, FlexBox } from "js/react/components/LayoutGrid";
import Spinner from "js/react/components/Spinner";
import UndoRedo from "js/react/components/UndoRedo";
import { dialogTheme } from "js/react/materialThemeOverrides";
import PresentationSettingsContainer from "js/react/views/PresentationSettings/PresentationSettingsContainer";
import RequestAccessDialog, { RequestAccessDialogContextType } from "js/react/views/RequestAccessDialog/RequestAccessDialog";
import { $, _ } from "js/vendor";

import EditorSkeleton from "./Components/EditorSkeleton";
import QuickStartMenu from "./Components/QuickStart/QuickStartMenu";
import { Button } from "../../Components/Button";
import { Divider } from "../../Components/Divider";
import { IconButton } from "../../Components/IconButton";
import { FlexSpacer, Gap10 } from "../../react/components/Gap";
import { themeColors } from "../../react/sharedStyles";
import SlideNavigatorPanel from "../ElementEditor/SlideNavigator/SlideNavigatorPanel";
import { FailedToRenderCanvasError } from "./CanvasController";
import { ClipboardController } from "./ClipboardController";
import { AddElementDropZone } from "./Components/AddElementDropZone";
import { CanvasWrapper } from "./Components/CanvasWrapper";
import CollaborationActions from "./Components/CollaborationActions";
import { BottomPanel, ElementPanelContainer, LEFT_PANEL_WIDTH, RightPanel, SIDE_MENU_BAR_WIDTH } from "./Components/EditorPanels";
import PresentationMenuBar, { MenuBarIconButton } from "./Components/PresentationMenuBar";
import ShortcutPanel from "./Components/ShortCutPanel";
import SlideActions from "./Components/SlideActions";
import { SlideBackgroundColorPicker } from "./Components/SlideBackgroundColorPicker";
import SpeakerNotes from "./Components/SpeakerNotes";
import PresentationEditorController, { PanelType } from "./PresentationEditorController";
import SelectionLayer from "./SelectionLayer";
import { TabKeyController } from "./TabKeyController";
import { calculateBoundingBox } from "./Components/QuickStart/utils/domUtils";

const logger = getLogger(LogGroup.EDITOR);

const SECONDARY_CANVAS_SCALE = 0.5;
const SECONDARY_SLIDE_OPACITY = 0.5;

const SLIDE_TRANSITION_DURATION_MS = 350;
const TRANSITION = `${SLIDE_TRANSITION_DURATION_MS}ms ease`;

const CONTROLS_HEIGHT = 70;

const SELECTION_LAYER_TRANSITION_DURATION_MS = 300;

const Container = styled.div`
    width: 100%;
    height: 100%;
    background: #4b4e55;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    
    &.light {
        background: #c8ced4;
        
        .slide-actions {
            .bai-icon, .micon {
                color: #333 !important;
            }
            label {
                color: #333;
            }
        }

        .MuiIcon-root {
            color: #515c66 !important;
        }
    }
`;

const InnerContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
`;

const EditorContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const CanvasContainer = styled.div`
    width: 100%;
    height: 100%;
    pointer-events: auto;
    position: absolute;
    margin-top: 30px;
`;

const RightCanvasControls = styled.div`
    position: absolute;
    width: 100px;
    height: ${props => props.canvasBounds.height}px;
    transform: ${props => `translateX(${props.canvasBounds.right}px) translateY(${props.canvasBounds.top}px)}`};
    opacity: ${props => props.visible ? 1 : 0};
    transition: ${props => props.transition ? TRANSITION : "none"};
    margin-top: 40px;

    .MuiIcon-root {
        color: white;
    }
`;

const LibrarySlideWarning = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: white;
    background: orangered;
    padding: 7px 16px 7px 8px;
    border-radius: 10px;
    display: flex;
    align-items: center;

    .bai-icon {
        color: white;
        margin-right: 5px;
    }
`;

const SkippedSlideWarning = styled(LibrarySlideWarning)`
    background: #ffaa00;
    padding-right: 8px;
`;

const PositionedCanvas = styled.div.attrs(({ layout, showTransition }) => ({
    style: {
        transform: `translateX(${layout.x}px) translateY(${layout.y}px) scale(${layout.scale})`,
        transition: showTransition ? `opacity 350ms, transform ${TRANSITION}` : "none",
        opacity: layout.opacity
    }
}))`
    position: absolute;
    transform-origin: 0 0;
    pointer-events: auto;
`;

const SelectionLayerContainer = styled.div.attrs(({ canvasBounds, visible }) => ({
    style: {
        opacity: visible ? 1 : 0,
        width: canvasBounds.width,
        height: canvasBounds.height,
        top: canvasBounds.top,
        left: canvasBounds.left,
        transition: `opacity ${visible ? SELECTION_LAYER_TRANSITION_DURATION_MS : 0}ms`
    }
}))`
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 9;
`;

export const SideMenuBarButton = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 5px 5px 5px;
    cursor: pointer;

    label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #ccc;
        margin-top: 3px;
        text-align: center;
        @media (height < 700px) {
            font-size: 9px;
            margin-top: 3px;
        }
        opacity: 0;
        transition: opacity 150ms;
    }
    
    &:hover {
        label {
            opacity: 1;
        }
    }

    .bai-icon {
        font-size: 23px;
        color: white !important;
        font-variation-settings: 'FILL' 0;
        @media (height < 700px) {
            font-size: 20px;
        }
    }
    
    .color-icon {
        width: 22px;
        @media (height < 700px) {
            width: 18px;
        }
    }


    &.selected {
        && {
            label {
                color: #51c6fa !important;
                opacity: 1;
            }

            .bai-icon {
                color: #51c6fa !important;
                font-variation-settings: 'FILL' 1;
            }
        }
    }
    &:hover {
        background: #333;
    }
`;

const SideMenuBar = styled.div`
    position: absolute;
    right: 0px;
    width: ${SIDE_MENU_BAR_WIDTH}px;
    height: 100%;
    background: #222;
    color: white;
    z-index: 5;
    padding-top: 10px;
    border-top: solid 1px #444;
    @media (height < 760px) {
        padding-top: 10px;
    }
    
    &.light {
        background: #fafafa;
        color: #333;
        
        .bai-icon {
            color: #515c66 !important;
        }
        border-top: none;
        border-left: solid 1px #ccc;
        .divider {
            background: #ccc !important;
        }

            ${SideMenuBarButton} {
                .bai-icon {
                    color: #515c66 !important;
                }
                label {
                    color: #444;
                }
                
                &.selected {
                    .bai-icon {
                        color: ${themeColors.ui_blue} !important;
                    }
                    label { 
                        color: ${themeColors.ui_blue} !important;
                    }
                }
                    
                
                &:hover {
                    background: #cdd7e4;
                }
            }
        }
    }
`;

const SideMenuBarContents = styled.div`
    //height: ${props => props.height + 20}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 0px;
    padding-top: 10px;
    align-items: center;
    @media (height < 760px) {
        //gap: 8px;
    }
    @media (height < 875px) {
        height: 100%;
    }
    
    .divider + div {
        padding-top: 15px;
    }
`;

const PresentationEditor = AppController.withState(
    PresentationEditorController.withState(
        class PresentationEditor extends Component {
            constructor() {
                super();

                this.canvasContainerRef = React.createRef();
                this.editorContainerRef = React.createRef();
                this.quickStartMenuRef = React.createRef();

                this.handleCurrentSlideChangePromiseChain = Promise.resolve();

                this.canvasWrappers = {};

                this.state = {
                    canvasWidth: 1280,
                    canvasHeight: 720,
                    slideLayoutProps: [],
                    canvasPadding: 140,
                    isReady: false,
                    availableBounds: new geom.Rect(0, 0, 0, 0),
                    primaryCanvasBounds: new geom.Rect(0, 0, 0, 0),
                    isTransitioningRightPanel: false,
                    bottomPanelHeight: 0,
                    completionPopoverState: null
                };

                this.throttledWheelEvent = _.throttle(
                    event => {
                        const { showSlideGrid, showEditorPopup, slideNavSelected, allowEditing } = this.props;

                        if (!allowEditing) return;
                        if (app.dialogManager.openDialogs.length || PresentationEditorController.getPopupOpenState()) return;
                        if (showSlideGrid || showEditorPopup || slideNavSelected) return;

                        if (event.originalEvent.deltaY < 0) {
                            PresentationEditorController.goPrevSlide();
                        } else if (event.originalEvent.deltaY > 0) {
                            PresentationEditorController.goNextSlide();
                        }
                    },
                    250,
                    { leading: true, trailing: false }
                );

                this.filteredWheelEvent = event => {
                    if (event.target.closest("#slide-navigator")) {
                        // don't navigate slides when mousewheel scrolling in the slide navigator
                        return;
                    }
                    // Magic Mouse generates a lot of low value deltas at even the smallest
                    //   gesture. MX Master mouse's smallest delta is 4.000244140625. So we
                    //   skip anything lower than 4 to verify intent to scroll.
                    const delta = Math.abs(event.originalEvent.deltaY);
                    if (delta < 4) {
                        return;
                    }

                    this.throttledWheelEvent(event);
                };
            }

            async componentDidMount() {
                logger.info("[PresentationEditor] mounted");

                const { onLoaded, presentation } = this.props;

                // Event handlers
                $(window).on("keydown.editor", this.handleKeyDown);
                $(window).on("resize.editor", this.handleResize);
                $(window).on("paste.editor", ClipboardController.onPaste);
                $(window).on("contextmenu.editor", this.handleContextMenu);

                $(window).on("wheel.editor", this.filteredWheelEvent);

                await this.setStateAsync({ isReady: true });

                await this.calculateSlideLayout(false);

                this.recordOpenCloseActivity("open");

                const {
                    template_recommendations: { enabled: hasTemplateRecommendations }
                } = await getExperiments(["template_recommendations"]);
                const props = {
                    source_presentation_id: presentation.get("sourcePresentationId") || "",
                    source_presentation_name: presentation.get("sourcePresentationName") || "",
                    is_from_recommended_template: presentation.get("metadata")?.isFromRecommendedTemplate ?? false,
                    object: "library_item",
                    object_label: "",
                    action: "clicked",
                    experiment_id: "5FC18A79E182FE7C764425D4F852F501",
                    experiment_group_assignment: hasTemplateRecommendations ? "variant-a" : "control"
                };

                trackActivity("Presentation", "Open", presentation.id, null, props, { audit: true });

                if (onLoaded) {
                    onLoaded();
                }
            }

            componentWillUnmount() {
                logger.info("[PresentationEditor] unmounting");

                this.recordOpenCloseActivity("close");

                $(window).off(".editor");
            }

            componentDidUpdate(prevProps, prevState, snapshot) {
                const { currentSlide, showPropertyPanel, showSlideNav, selectedPropertyPanelTabProps, slides } = this.props;
                const { transition, isReady } = this.state;

                if (prevProps.currentSlide !== currentSlide) {
                    this.handleCurrentSlideChange();
                    return;
                }

                if (prevProps.slides !== slides) {
                    this.calculateSlideLayout(false);
                    // Check if slide was added and task was completed
                    // Note: previousTaskStatus is captured before showAddSlideDialog is called
                    this.checkAndShowCompletionPopover(isReady);
                    return;
                }

                if (
                    (showSlideNav !== prevProps.showSlideNav) ||
                    (selectedPropertyPanelTabProps?.width !== prevProps.selectedPropertyPanelTabProps?.width)
                ) {
                    if (!transition) {
                        this.calculateSlideLayout(true);
                    }
                } else if (prevProps.slideNavWidth !== this.props.slideNavWidth) {
                    this.calculateSlideLayout(!this.props.isUserResizingSlideNav);
                } else if (showPropertyPanel !== prevProps.showPropertyPanel) {
                    this.calculateSlideLayout(true);
                }

                // enable to turn on selection opening closed edit panel - disabled for now
                // if (this.props.currentSelectionLayerController?.selectedElements.length && !showPropertyPanel) {
                //     PresentationEditorController.setSelectedPropertyPanelTab("element");
                // }
            }

            checkAndShowCompletionPopover = async isReady => {
                // Only show if editor is ready and we haven't shown it yet
                if (!isReady || this.state.completionPopoverState) {
                    return;
                }

                // Position to the left of #control-bar-container
                // Wait a bit for control bar to be rendered
                setTimeout(() => {
                    const boundingBox = calculateBoundingBox("#control-bar-container", 8);

                    if (!boundingBox) {
                        return;
                    }

                    const { top, height, left } = boundingBox;

                    this.setState({
                        completionPopoverState: {
                            position: {
                                top: top + height / 2,
                                left: left
                            },
                            transformOrigin: { vertical: "center", horizontal: "right" }
                        }
                    });
                }, 100);
            }

            handleCloseCompletionPopover = () => {
                this.setState({ completionPopoverState: null });
                // Trigger completion check in QuickStartMenu
                if (this.quickStartMenuRef.current) {
                    this.quickStartMenuRef.current.checkAndShowCompletedStep();
                }
            }

            handleCurrentSlideChange() {
                // Keeping the current slide id to keep track of its changes
                const currentSlideId = this.props.currentSlide.id;

                // Keep track of the current slide change request for metrics
                this.currentSlideChangeRequest = {
                    slideId: currentSlideId,
                    timestamp: Date.now()
                };

                return new Promise((resolve, reject) => {
                    this.handleCurrentSlideChangePromiseChain = this.handleCurrentSlideChangePromiseChain
                        .then(async () => {
                            if (this.props.currentSlide.id !== currentSlideId) {
                                // Current slide changed while we were waiting for the prev call
                                return;
                            }

                            await Promise.all([
                                PresentationEditorController.hideCanvasControls(),
                                this.calculateSlideLayout(true)
                            ]);
                        })
                        .then(resolve)
                        .catch(reject);
                });
            }

            setStateAsync(stateUpdate) {
                return new Promise(resolve => this.setState(stateUpdate, resolve));
            }

            recordOpenCloseActivity = async action => {
                const { presentation } = this.props;

                if (
                    !this.state.isReady ||
                    this.props.isSingleSlideEditor ||
                    isPPTAddin ||
                    presentation.disconnected ||
                    presentation.get("isDummy")
                ) return;

                try {
                    const {
                        template_recommendations: { enabled: hasTemplateRecommendations }
                    } = await getExperiments(["template_recommendations"]);

                    await presentationsApi.recordActivity({
                        id: this.props.presentation.id,
                        activity: action === "open"
                            ? PresentationActivityType.EDITOR_OPENED
                            : PresentationActivityType.EDITOR_CLOSED,
                        activityData: action === "open" ? {
                            source_presentation_id: presentation.get("sourcePresentationId") || "",
                            source_presentation_name: presentation.get("sourcePresentationName") || "",
                            is_from_recommended_template: presentation.get("metadata")?.isFromRecommendedTemplate ?? false,
                            object: "library_item",
                            object_label: "",
                            action: "clicked",
                            experiment_id: "5FC18A79E182FE7C764425D4F852F501",
                            experiment_group_assignment: hasTemplateRecommendations ? "variant-a" : "control"
                        } : null
                    });
                } catch (err) {
                    logger.error(err, "[PresentationEditor] failed to record activity");
                }
            }

            handleKeyDown = event => {
                const { presentation, currentCanvasController, currentSelectionLayerController, showSlideGrid, handleKeyEvents, showFindAndReplaceBar } = this.props;
                const isCmdZ = (event.metaKey || event.ctrlKey) && event.which === Key.KEY_Z;
                const isPopupOpen = PresentationEditorController.getPopupOpenState();

                // Only Cmd/Ctrl+Z (undo/redo) should work when popup is open
                const isUndoRedoShortcut = isCmdZ;

                if (
                    showFindAndReplaceBar ||
                    showSlideGrid ||
                    app.dialogManager.openDialogs.length ||
                    (!isUndoRedoShortcut && isPopupOpen) ||
                    !handleKeyEvents ||
                    (
                        // Block inputs/contentEditable normally, but allow Cmd/Ctrl+Z through when not editing text
                        !isCmdZ && (event.target.tagName === "INPUT" && event.target.type !== "checkbox" || event.target.contentEditable === "true") ||
                        (isCmdZ && app.isEditingText)
                    )
                ) {
                    return;
                }

                switch (event.which) {
                    case Key.DELETE:
                    case Key.BACKSPACE:
                        let handleKeyboardShortcut;
                        if (currentSelectionLayerController.selectedElements.length === 1) {
                            handleKeyboardShortcut = currentSelectionLayerController.selectedElements[0].getSelectionElement()?.uiRefs.selectionRef.current?.handleKeyboardShortcut;
                        } else {
                            handleKeyboardShortcut = currentCanvasController.canvas.layouter.elements.primary.getSelectionElement()?.uiRefs.selectionRef.current?.handleKeyboardShortcut;
                        }
                        if (handleKeyboardShortcut) {
                            handleKeyboardShortcut(event);
                        }
                        event.preventDefault();
                        return;
                    case Key.LEFT_ARROW:
                    case Key.UP_ARROW:
                    case Key.PAGE_UP:
                        event.preventDefault();
                        PresentationEditorController.goPrevSlide();
                        PresentationEditorController.trackSlideNavigation({ method: "tab" });
                        return;
                    case Key.RIGHT_ARROW:
                    case Key.DOWN_ARROW:
                    case Key.PAGE_DOWN:
                        event.preventDefault();
                        PresentationEditorController.goNextSlide();
                        PresentationEditorController.trackSlideNavigation({ method: "tab" });
                        return;
                    case Key.HOME:
                        event.preventDefault();
                        this.goToSlide(0);
                        return;
                    case Key.END:
                        event.preventDefault();
                        this.goToSlide(presentation.slides.length - 1);
                        return;
                    case Key.TAB:
                        TabKeyController.handleTabKey(event);
                        return;
                    case Key.SPACE:
                        event.preventDefault();
                        if (PresentationEditorController._state.slideNavMode != "nav") {
                            PresentationEditorController.setSlideNavMode("nav");
                        } else {
                            PresentationEditorController.setSlideNavMode("grid");
                        }
                        return;
                    case Key.ESCAPE:
                        if (PresentationEditorController._state.slideNavMode != "nav") {
                            PresentationEditorController.setSlideNavMode("nav");
                        }
                        return;
                }

                if (event.metaKey || event.ctrlKey) {
                    switch (event.which) {
                        case Key.KEY_F:
                            PresentationEditorController.handleShowSearchForText(true);
                            event.stopPropagation();
                            event.preventDefault();
                            return;
                        case Key.KEY_Z:
                            if (event.shiftKey) {
                                PresentationEditorController.redo();
                            } else {
                                PresentationEditorController.undo();
                            }
                            event.stopPropagation();
                            event.preventDefault();
                            return;
                        case Key.KEY_E:
                            if (event.metaKey || event.ctrlKey && event.altKey) {
                                app.themeManager.loadTheme(presentation)
                                    .then(() => currentCanvasController.canvas.loadStyles(true))
                                    .then(() => currentSelectionLayerController.setSelectedElements([]))
                                    .then(() => {
                                        currentCanvasController.canvas.getCanvasElement().markStylesAsDirty();
                                        currentCanvasController.canvas.clearAuthoringBlockPropsCache();
                                    })
                                    .then(() => currentCanvasController.reloadCanvas(null, true));
                            }
                            break;
                        case Key.KEY_N:
                        case Key.KEY_A:
                            PresentationEditorController.showAddSlideDialog();
                            event.preventDefault();
                            return;
                        case Key.KEY_D:
                            ClipboardController.onDuplicate(event);
                            event.preventDefault();
                            return;
                        case Key.KEY_C:
                        case Key.KEY_X:
                            ClipboardController.onCopyOrCut(event);
                            return;
                        case Key.KEY_S:
                            event.preventDefault();
                            ShowMessageDialog({
                                title: "Your presentation is saved automatically",
                                message: <>
                                    <p>Beautiful.ai saves your work automatically as you make edits. There is no need to manually save.</p>
                                    <p>You can use the undo/redo buttons or Cmd/Ctrl-Z to undo any recent edits or revert to a previous version of a slide from the Manage Slide menu</p>
                                </>
                            });
                            return;
                        case Key.FORWARD_SLASH:
                            if (AppController.externalActivePanel === PanelType.SHORTCUTS) {
                                AppController.setExternalActivePanel(null);
                            } else {
                                AppController.setExternalActivePanel(PanelType.SHORTCUTS);
                            }
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                    }
                }
            }

            handleResize = () => {
                this.calculateSlideLayout(false);
            }

            handleContextMenu = event => {
                if (isDevelopment && event.altKey) {
                    $(".current_slide").addClass("dev-click-suppress");
                    $(".current_slide").find("div").addClass("dev-right-click");
                    $(".default-overlay-container").find("div").addBack().removeClass("dev-right-click").addClass("dev-click-suppress");
                    $("#callouts, #connectors").removeClass("dev-right-click").addClass("dev-click-suppress");
                    return;
                }

                // don't prevent contextMenu on contentEditables
                if ($(event.target).closest("[contenteditable = true]").length) return;

                if (app.isEditingText) return;

                event.preventDefault();
                event.stopPropagation();
            }

            goToSlide = index => PresentationEditorController.setCurrentSlideByIndex(index)

            removeFileDropOverlay = $editorContainer => {
                $editorContainer.off("dragover drop dragenter dragleave");
            }

            calculateSlideLayout = async transition => {
                const { slides, currentSlide, activeBottomPanel, canvasControllers, isSingleSlideEditor, showSlideNav, slideNavWidth, showPropertyPanel, selectedPropertyPanelTabProps } = this.props;
                const { canvasWidth, canvasHeight, bottomPanelHeight, slideLayoutProps: prevSlideLayoutProps } = this.state;

                let canvasPaddingLeft, canvasPaddingRight;

                if (isSingleSlideEditor) {
                    canvasPaddingLeft = 40;
                    canvasPaddingRight = 40;
                } else {
                    canvasPaddingLeft = showSlideNav ? 30 : 140;
                    canvasPaddingRight = 100;
                }

                const currentSlideIndex = slides.indexOf(currentSlide);

                const calcPrimaryCanvasScale = availableBounds => {
                    const controlSpacing = 90;
                    let scale = Math.min((availableBounds.height - controlSpacing) / canvasHeight, availableBounds.width / canvasWidth);
                    scale = Math.min(scale, 2);
                    return scale;
                };

                const $canvasContainer = $(this.canvasContainerRef.current);
                const $editorContainerRef = $(this.editorContainerRef.current);
                let containerBounds = new geom.Rect(0, 0, $canvasContainer.width() - SIDE_MENU_BAR_WIDTH, $canvasContainer.height() - 20);

                if (showSlideNav && !isSingleSlideEditor) {
                    containerBounds = containerBounds.deflate({ left: slideNavWidth });
                }

                if (showPropertyPanel) {
                    containerBounds = containerBounds.deflate({ right: (selectedPropertyPanelTabProps?.width ?? LEFT_PANEL_WIDTH) });
                }

                this.removeFileDropOverlay($editorContainerRef);

                let availableBounds = containerBounds.deflate({ left: canvasPaddingLeft, right: canvasPaddingRight });

                let primaryCanvasScale = calcPrimaryCanvasScale(availableBounds);
                let primaryCanvasSize = new geom.Size(canvasWidth, canvasHeight).scale(primaryCanvasScale);
                let primaryCanvasBounds = new geom.Rect(availableBounds.centerH - primaryCanvasSize.width / 2, containerBounds.top, primaryCanvasSize);

                if (activeBottomPanel && bottomPanelHeight) {
                    if (bottomPanelHeight > availableBounds.height - primaryCanvasBounds.bottom - CONTROLS_HEIGHT) {
                        availableBounds = availableBounds.deflate({ bottom: bottomPanelHeight - 40 });
                    }
                }

                // recalculate primary canvas props in case availableBounds was changed by panels
                primaryCanvasScale = calcPrimaryCanvasScale(availableBounds);
                primaryCanvasSize = new geom.Size(canvasWidth, canvasHeight).scale(primaryCanvasScale);
                primaryCanvasBounds = new geom.Rect(availableBounds.centerH - primaryCanvasSize.width / 2, availableBounds.top, primaryCanvasSize);

                // calculate secondary canvas props
                const secondaryCanvasScale = primaryCanvasScale * SECONDARY_CANVAS_SCALE;
                const secondaryCanvasWidth = canvasWidth * secondaryCanvasScale;

                // set starting x position scrolled to current slide
                let x = primaryCanvasBounds.left - currentSlideIndex * (secondaryCanvasWidth + (showPropertyPanel ? 30 : 100));

                // reset wrappers
                this.canvasWrappers = {};

                let previousSlideIsCurrentSlide = false;

                // go through the slides and build layout props
                const slideLayoutProps = [];
                for (const slide of slides) {
                    const slideLayout = {
                        id: slide.id,
                        model: slide
                    };

                    if (slide === currentSlide) {
                        slideLayout.scale = primaryCanvasScale;
                        slideLayout.opacity = 1;
                        slideLayout.blur = 0;
                        slideLayout.isCurrent = true;
                        previousSlideIsCurrentSlide = true;
                    } else {
                        slideLayout.scale = primaryCanvasScale * SECONDARY_CANVAS_SCALE;
                        slideLayout.opacity = showSlideNav ? 0 : SECONDARY_SLIDE_OPACITY;

                        slideLayout.blur = 10;
                    }

                    slideLayout.x = x;
                    slideLayout.y = (availableBounds.top + (primaryCanvasBounds.height - (canvasHeight * slideLayout.scale)) / 2).toFixed(3);
                    slideLayout.width = canvasWidth * slideLayout.scale;
                    slideLayout.height = canvasHeight * slideLayout.scale;

                    if (previousSlideIsCurrentSlide) {
                        x += canvasWidth * slideLayout.scale + 100;
                        previousSlideIsCurrentSlide = false;
                    } else {
                        if (showPropertyPanel) {
                            x += canvasWidth * slideLayout.scale + 30;
                        } else {
                            x += canvasWidth * slideLayout.scale + 100;
                        }
                    }

                    slideLayoutProps.push(slideLayout);

                    const canvasController = canvasControllers[slideLayout.id];

                    // We need to set the canvasScale for each canvas
                    canvasController.canvasScale = slideLayout.scale;
                    canvasController.maxCanvasScale = Math.max(primaryCanvasScale, slideLayout.scale);
                }

                const slideLayoutPropsKeys = ["x", "y", "width", "height"];
                const prevCurrentSlideLayoutProps = _.pick(prevSlideLayoutProps.find(layout => layout.model === currentSlide) ?? {}, slideLayoutPropsKeys);
                const newCurrentSlideLayoutProps = _.pick(slideLayoutProps.find(layout => layout.model === currentSlide) ?? {}, slideLayoutPropsKeys);
                if (_.isEqual(prevCurrentSlideLayoutProps, newCurrentSlideLayoutProps)) {
                    transition = false;
                }

                const stateUpdates = {
                    slideLayoutProps,
                    primaryCanvasBounds,
                    primaryCanvasScale,
                    transition,
                    availableBounds,
                    containerBounds,
                };

                if (!showPropertyPanel) {
                    stateUpdates.sideBarHeight = primaryCanvasBounds.height;
                }

                await this.setStateAsync(stateUpdates);

                if (!transition) {
                    await this.handleCanvasTransitionEnded();
                }
            }

            handleCanvasMouseDown = (event, slideLayoutModel, isCurrentCanvas) => {
                const { currentCanvasController, isSingleSlideEditor } = this.props;

                if (currentCanvasController.isTemplateObsolete && isCurrentCanvas) {
                    event.stopPropagation();
                    event.preventDefault();
                    return;
                }

                if (!isCurrentCanvas) {
                    event.stopPropagation();
                    PresentationEditorController.setCurrentSlide(slideLayoutModel);
                    return;
                }

                if (slideLayoutModel.isLibrarySlide() && !isSingleSlideEditor) {
                    event.stopPropagation();
                    currentCanvasController.editLibrarySlide();
                }
            }

            handleCanvasTransitionEnded = async () => {
                const { showCanvasControls, currentSlide, presentation } = this.props;
                const { transition } = this.state;

                if (transition) {
                    this.setState({ transition: false });
                }

                if (!showCanvasControls) {
                    await PresentationEditorController.showCanvasControls();
                }

                if (this.currentSlideChangeRequest?.slideId === currentSlide.id) {
                    logger.metric(MetricName.ADVANCE_TO_SLIDE_TIME, {
                        presentationId: presentation.id,
                        slideId: currentSlide.id,
                        advanceTimeMs: Date.now() - this.currentSlideChangeRequest.timestamp
                    });
                }

                this.currentSlideChangeRequest = null;
            }

            handleRightPanelTransitionState = transitionState => {
                // this.setState({
                //     isTransitioningRightPanel: ["entering", "exiting"].includes(transitionState)
                // });
            }

            handleBottomPanelTransitionState = (transitionState, bottomPanelHeight) => {
                this.setState({
                    bottomPanelHeight,
                }, () => {
                    if (["entering", "exiting"].includes(transitionState)) {
                        this.calculateSlideLayout(true);
                    }
                });
            }

            handleBottomPanelResizeState = (state, bottomPanelHeight) => {
                this.setState({
                    bottomPanelHeight,
                });

                this.calculateSlideLayout(false);
            }

            handleCloseRightPanel = () => {
                PresentationEditorController.closeRightPanel();
            }

            handleCloseBottomPanel = () => {
                PresentationEditorController.closeBottomPanel();
            }

            handleCloseSlideGrid = async focusedSlide => {
                await PresentationEditorController.toggleSlideGrid();
                await PresentationEditorController.setCurrentSlide(focusedSlide);
            }

            handleToggleNotes = () => {
                PresentationEditorController.toggleNotes();
            }

            handleShowNarration = () => {
                PresentationEditorController.showPropertyPanel();
                PresentationEditorController.setSelectedPropertyPanelTab("animate");
            }

            handleShowSlideHelp = () => {
                const { currentCanvasController } = this.props;
                let TemplateHelp = currentCanvasController.canvas.getPrimaryElement().getElementHelp();

                ShowDialog(TemplateHelp);
            }

            handleSetPanel = async panelType => {
                const { showFindAndReplaceBar } = this.props;
                const selectedPropertyPanelTabProps = {};

                let width;
                switch (panelType) {
                    case "slide":
                        selectedPropertyPanelTabProps.width = 400;
                        width = 400;
                        break;
                    case "comments":
                        selectedPropertyPanelTabProps.goToSlide = index => this.goToSlide(index);
                        break;
                    default:
                        width = LEFT_PANEL_WIDTH;
                        break;
                }

                if (showFindAndReplaceBar) {
                    await PresentationEditorController.handleShowSearchForText(false);
                }

                if (PresentationEditorController.selectedPropertyPanelTab === panelType) {
                    PresentationEditorController.hidePropertyPanel();
                } else {
                    PresentationEditorController.setSelectedPropertyPanelTab(panelType, selectedPropertyPanelTabProps);
                }
            }

            render() {
                const {
                    presentation,
                    activeBottomPanel,
                    hidePanels,
                    currentSlide,
                    showCanvasControls,
                    showSelectionLayer,
                    currentCanvasController,
                    currentSelectionLayerController,
                    isSingleSlideEditor,
                    allowSharedSlideEditing,
                    showAddElementDropZone,
                    canvasControllers,
                    allowSwitchSlide,
                    showPropertyPanel,
                    selectedPropertyPanelTab,
                    selectedPropertyPanelTabProps,
                    currentSlideLockOwner,
                    isCurrentSlideLockedForMe,
                    allowStyleChange,
                    uiTheme,
                    showSlideNav,
                    slideNavMode,
                    slideNavWidth
                } = this.props;

                const {
                    availableBounds,
                    containerBounds,
                    isReady,
                    slideLayoutProps,
                    primaryCanvasBounds,
                    transition,
                    sideBarHeight
                } = this.state;

                if (!isReady) {
                    return <Spinner />;
                }

                const isSelectionLayerVisible = (
                    showSelectionLayer &&
                    !transition
                );

                const availableSpaceBelowPrimaryCanvas = availableBounds.height - primaryCanvasBounds.bottom;

                const BottomPanels = [
                    {
                        type: PanelType.ELEMENT,
                        height: Math.max(availableSpaceBelowPrimaryCanvas - CONTROLS_HEIGHT, 300),
                        children: <ElementPanelContainer {...this.props}
                            onClose={this.handleCloseBottomPanel}
                        />
                    },
                    {
                        type: PanelType.NOTES,
                        height: Math.max(availableSpaceBelowPrimaryCanvas - CONTROLS_HEIGHT, 300),
                        children: <SpeakerNotes {...this.props}
                            onClose={this.handleCloseBottomPanel}
                        />
                    }
                ]
                    .filter(({ type }) => !hidePanels.includes(type))
                    .map(({ type, height, children }) => (
                        <BottomPanel
                            key={type}
                            visible={activeBottomPanel === type}
                            showPropertyPanel={showPropertyPanel}
                            height={height}
                            containerBounds={containerBounds}
                            reportTransitionState={this.handleBottomPanelTransitionState}
                            reportResizeState={this.handleBottomPanelResizeState}
                            onClose={this.handleCloseBottomPanel}
                        >
                            {children}
                        </BottomPanel>
                    ));

                return (
                    <DndProvider backend={HTML5Backend}>
                        <Container className={uiTheme}>
                            <ThemeProvider theme={dialogTheme}>
                                {!isSingleSlideEditor && <PresentationMenuBar slideNavWidth={slideNavWidth} uiTheme={uiTheme} />}
                            </ThemeProvider>
                            <InnerContainer>
                                <EditorContainer id="editor-container"
                                    ref={this.editorContainerRef}
                                >
                                    <CanvasContainer id="canvas-container"
                                        ref={this.canvasContainerRef}
                                    >
                                        {primaryCanvasBounds && (
                                            <>
                                                {slideLayoutProps.map(slideLayout => {
                                                    if (!this.canvasWrappers[slideLayout.id]) {
                                                        this.canvasWrappers[slideLayout.id] = canvasControllers[slideLayout.id].withState(CanvasWrapper);
                                                    }
                                                    const CanvasWrapperWithState = this.canvasWrappers[slideLayout.id];
                                                    const isCurrentCanvas = slideLayout.model === currentSlide;

                                                    return (
                                                        <PositionedCanvas
                                                            key={slideLayout.id}
                                                            className="positioned-canvas"
                                                            layout={slideLayout}
                                                            showTransition={transition}
                                                            onTransitionEnd={isCurrentCanvas ? this.handleCanvasTransitionEnded : () => { }}
                                                            onMouseDown={event => this.handleCanvasMouseDown(event, slideLayout.model, isCurrentCanvas)}
                                                        >
                                                            <CanvasWrapperWithState />
                                                        </PositionedCanvas>
                                                    );
                                                })}

                                                {primaryCanvasBounds && currentSlide && (
                                                    <SelectionLayerContainer
                                                        canvasBounds={primaryCanvasBounds}
                                                        visible={isSelectionLayerVisible}
                                                    >
                                                        <SelectionLayer isSingleSlideEditor={isSingleSlideEditor} />
                                                    </SelectionLayerContainer>
                                                )}

                                                {showAddElementDropZone && (
                                                    <AddElementDropZone
                                                        currentCanvasController={currentCanvasController}
                                                        currentSelectionLayerController={currentSelectionLayerController}
                                                    />
                                                )}

                                                {showCanvasControls && currentSlide.has("audioAsset") && (
                                                    <AbsoluteBox left={primaryCanvasBounds.right - 60} top={primaryCanvasBounds.bottom + 14}>
                                                        <MenuBarIconButton onClick={this.handleShowNarration}>
                                                            <Icon fill>record_voice_over</Icon>
                                                            <label className="hover-label">Slide Narration</label>
                                                        </MenuBarIconButton>
                                                    </AbsoluteBox>
                                                )}

                                                {showCanvasControls && (
                                                    <AbsoluteBox left={primaryCanvasBounds.left + 5} top={primaryCanvasBounds.bottom + 15}>
                                                        <UndoRedo />
                                                    </AbsoluteBox>
                                                )}

                                                {(currentSlide?.isLibrarySlide() || presentation.isSlideSkipped(currentSlide)) && !isSingleSlideEditor && (
                                                    <AbsoluteBox left={primaryCanvasBounds.left} top={primaryCanvasBounds.top - 17} width={primaryCanvasBounds.width}>
                                                        <FlexBox fillWidth gap={20}>
                                                            {presentation.isSlideSkipped(currentSlide) &&
                                                                <SkippedSlideWarning>
                                                                    <Icon fill white>visibility_off</Icon>
                                                                    Slide skipped during playback
                                                                    <Gap10 />
                                                                    <Button unframed height={20}
                                                                        onClick={() => PresentationEditorController.setSkipSlide(currentSlide, false)}
                                                                    >Unskip</Button>
                                                                </SkippedSlideWarning>
                                                            }
                                                            {currentSlide.isLibrarySlide() &&
                                                                <LibrarySlideWarning>
                                                                    <Icon fill white>lock</Icon>
                                                                    This is a team shared slide and cannot be edited directly
                                                                </LibrarySlideWarning>
                                                            }
                                                        </FlexBox>
                                                    </AbsoluteBox>
                                                )}

                                                {isCurrentSlideLockedForMe && (
                                                    <AbsoluteBox left={primaryCanvasBounds.left} top={primaryCanvasBounds.top - 17} width={primaryCanvasBounds.width}>
                                                        <FlexBox fillWidth>
                                                            <LibrarySlideWarning>
                                                                <Icon fill white>lock</Icon>{currentSlideLockOwner.displayName} is editing this slide
                                                            </LibrarySlideWarning>
                                                        </FlexBox>
                                                    </AbsoluteBox>
                                                )}

                                            </>
                                        )}
                                    </CanvasContainer>

                                    {primaryCanvasBounds && !isSingleSlideEditor && (
                                        <RightCanvasControls
                                            canvasBounds={primaryCanvasBounds}
                                            visible={showCanvasControls}
                                            transition={transition}
                                        >
                                            <SlideActions uiTheme={uiTheme} />
                                        </RightCanvasControls>
                                    )}

                                    <ShortcutPanel />

                                    <SideMenuBar id="side-menu-bar" className={uiTheme} onMouseDown={event => event.stopPropagation()}>
                                        <SideMenuBarContents height={sideBarHeight}>
                                            <SlideBackgroundColorPicker currentCanvasController={currentCanvasController}
                                                size={20}
                                            />
                                            <SideMenuBarButton onMouseDown={() => this.handleSetPanel("add")}
                                                className={showPropertyPanel && selectedPropertyPanelTab === "add" && "selected"}
                                            >
                                                <Icon white>table_chart</Icon>
                                                <label>Layout</label>
                                            </SideMenuBarButton>
                                            <SideMenuBarButton onMouseDown={() => this.handleSetPanel("element")}
                                                className={(showPropertyPanel && selectedPropertyPanelTab === "element") && "selected"}
                                            >
                                                <Icon white>app_registration</Icon>
                                                <label>Edit</label>
                                            </SideMenuBarButton>
                                            <SideMenuBarButton onMouseDown={() => this.handleSetPanel("ideas")}
                                                className={(showPropertyPanel && selectedPropertyPanelTab === "ideas") && "selected"}
                                            >
                                                <Icon white>emoji_objects</Icon>
                                                <label>Ideas</label>
                                            </SideMenuBarButton>
                                            <SideMenuBarButton onMouseDown={() => this.handleSetPanel("animate")}
                                                className={(showPropertyPanel && selectedPropertyPanelTab === "animate") && "selected"}
                                            >
                                                <Icon white>play_circle</Icon>
                                                <label>Playback</label>
                                            </SideMenuBarButton>
                                            <FlexSpacer />
                                            <Divider color="#444" />
                                            <SideMenuBarButton onMouseDown={() => this.handleSetPanel("manage-slide")}
                                                className={(showPropertyPanel && selectedPropertyPanelTab === "manage-slide") && "selected"}
                                            >
                                                <Icon white>settings</Icon>
                                                <label>Manage</label>
                                            </SideMenuBarButton>
                                            {!hidePanels.includes(PanelType.ASSIGN_SLIDE) &&
                                                <>
                                                    <Divider color="#444" />
                                                    <SideMenuBarButton>
                                                        <CollaborationActions />
                                                        <label>Assign</label>
                                                    </SideMenuBarButton>
                                                </>
                                            }
                                            {!hidePanels.includes(PanelType.COMMENTS) &&
                                                <SideMenuBarButton
                                                    data-test-id="show-comments"
                                                    onMouseDown={() => this.handleSetPanel("comments")}
                                                    className={(showPropertyPanel && selectedPropertyPanelTab === "comments") && "selected"}
                                                >
                                                    <Icon white>maps_ugc</Icon>
                                                    <label>Comments</label>
                                                </SideMenuBarButton>}
                                            {!hidePanels.includes(PanelType.NOTES) &&
                                                <>
                                                    <Divider color="#444" />
                                                    <SideMenuBarButton data-test-id="speaker-notes-icon" onMouseDown={this.handleToggleNotes}
                                                        className={activeBottomPanel == PanelType.NOTES && "selected"}
                                                    >
                                                        <Icon white>speaker_notes</Icon>
                                                        <label>Notes</label>
                                                    </SideMenuBarButton>
                                                </>
                                            }
                                        </SideMenuBarContents>
                                    </SideMenuBar>

                                    <ThemeProvider theme={dialogTheme}>
                                        {!isSingleSlideEditor && (
                                            <SlideNavigatorPanel />
                                        )}
                                        {!showSlideNav && !isSingleSlideEditor && (
                                            <AbsoluteBox left="15" top="10">
                                                <FlexBox vertical gap={0}>
                                                    <IconButton icon="chevron_right"
                                                        color={uiTheme == "dark" ? "#eee" : "#333"} hoverColor={uiTheme == "dark" ? "#333" : "#eee"}
                                                        onClick={() => PresentationEditorController.showSlideNav()}
                                                    />
                                                    <IconButton icon="grid_view"
                                                        color={uiTheme == "dark" ? "#eee" : "#333"} hoverColor={uiTheme == "dark" ? "#333" : "#eee"}
                                                        onClick={() => PresentationEditorController.setSlideNavMode("grid")}
                                                    />
                                                </FlexBox>
                                            </AbsoluteBox>
                                        )}

                                        <RightPanel visible={showPropertyPanel}
                                            open={showPropertyPanel}
                                            width={selectedPropertyPanelTabProps?.width ?? 280}
                                            reportTransitionState={this.handleRightPanelTransitionState}
                                        >
                                            <EditorPropertyPanel isSingleSlideEditor={isSingleSlideEditor}
                                                allowStyleChange={allowStyleChange}
                                                allowSwitchSlide={allowSwitchSlide}
                                                allowSharedSlideEditing={allowSharedSlideEditing}
                                            />
                                        </RightPanel>
                                        {BottomPanels}
                                    </ThemeProvider>
                                </EditorContainer>
                                <QuickStartMenu ref={this.quickStartMenuRef} />
                                {this.renderCompletionPopover()}
                            </InnerContainer>
                        </Container>
                    </DndProvider>
                );
            }

            renderCompletionPopover = () => {
                const { completionPopoverState } = this.state;

                if (!completionPopoverState) {
                    return null;
                }

                return (
                    <TaskRendererFactory
                        type="popover"
                        taskKey={QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE_END}
                        popoverState={completionPopoverState}
                        onPopoverClose={this.handleCloseCompletionPopover}
                    />
                );
            }
        }
    )
);

const PresentationEditorInitializer =
    PresentationEditorController.withState(
        function PresentationEditorInitializer({
            presentationId,
            initialSlideIndex,
            isSingleSlideEditor,
            onLoaded,
            hidePanels,
            allowThemeChange,
            allowSharedSlideEditing = false,
            renderSettings,
            pane,
            dummyPresentation = null,
            allowStyleChange = true,
            allowSwitchSlide = true,
            allowEditing = true,
            // Comes from PresentationEditorController
            presentation,
            isInitialized,
        }) {
            useEffect(() => {
                const name = presentation?.get("name");
                if (name) {
                    document.title = "Beautiful.ai - " + name;
                } else {
                    document.title = "Beautiful.ai";
                }

                return () => {
                    document.title = "Beautiful.ai";
                };
            }, [presentation?.get("name")]);

            useEffect(() => {
                const loadStartTime = Date.now();

                (async () => {
                    // If we are on mobile, we need to load the presentation in the
                    // presentation player mode and not editor
                    if (app.isMobileOrTablet) {
                        AppController.playPresentation({ presentationId, slideIndex: initialSlideIndex });
                        return;
                    }

                    if (!presentationId) {
                        await PresentationEditorController.reset();
                        return;
                    }

                    let presentation;
                    if (dummyPresentation) {
                        presentation = dummyPresentation;
                    } else if (PresentationEditorController.preloadedPresentation && PresentationEditorController.preloadedPresentation.id === presentationId) {
                        presentation = PresentationEditorController.preloadedPresentation;
                        PresentationEditorController.preloadedPresentation = null;
                    } else {
                        presentation = await getPresentation(presentationId, { permission: "write", autoSync: true });
                    }

                    // If collaborator with read-only access, redirect to player
                    if (presentation.permissions.read && !presentation.permissions.write) {
                        AppController.playPresentation({ presentationId, slideIndex: initialSlideIndex }, { goBackToLibrary: true });
                        return;
                    }

                    await PresentationEditorController.loadPresentation(presentation, initialSlideIndex, isSingleSlideEditor, allowThemeChange, allowEditing, hidePanels);

                    if (renderSettings) {
                        ShowDialog(PresentationSettingsContainer, { startPane: pane, presentation });
                    }

                    logger.metric(MetricName.EDITOR_LOAD_TIME, {
                        presentationId,
                        loadTimeMs: Date.now() - loadStartTime,
                        loadFailed: false
                    });
                })().catch(err => {
                    logger.error(err, "[PresentationEditorInitializer] failed to load presentation", err);

                    if (err instanceof PresentationPermissionDeniedError) {
                        ShowDialog(RequestAccessDialog, {
                            presentationOrLinkId: presentationId,
                            contextType: RequestAccessDialogContextType.EDITOR
                        });
                    } else if (err instanceof PresentationNotFoundError) {
                        ShowErrorDialog({
                            title: "Presentation not found",
                            message: "Presentation not found, make sure your link is correct.",
                            acceptCallback: () => AppController.showLibrary()
                        });
                    } else if (err instanceof FailedToRenderCanvasError) {
                        ShowErrorDialog({
                            title: "Failed to render presentation",
                            message: <>
                                Sorry, we couldn't load some slides in your presentation, please contact us
                                at
                                <a href="mailto:support@beautiful.ai">support@beautiful.ai</a>
                            </>,
                            acceptCallback: () => AppController.showLibrary()
                        });
                    } else {
                        ShowErrorDialog({
                            title: "Failed to load presentation",
                            message: "Sorry, something went wrong, please try again later.",
                            acceptCallback: () => AppController.showLibrary()
                        });
                    }

                    logger.metric(MetricName.EDITOR_LOAD_TIME, {
                        presentationId,
                        loadTimeMs: Date.now() - loadStartTime,
                        loadFailed: true
                    });
                });

                return () => {
                    (async () => {
                        // First, mark the presentation as dirty so that the thumbnails show spinners
                        if (PresentationLibraryController.isInitialized) {
                            PresentationLibraryController.markPresentationsDirty([presentationId]);
                        }

                        // Controller reset will trigger finishedEditing for current slide that may update the presentation
                        // timestamps
                        await PresentationEditorController.reset();

                        // Force refresh the presentation in the library so that the timestamps and thumbnails are updated
                        if (PresentationLibraryController.isInitialized) {
                            PresentationLibraryController.forceRefreshPresentations([presentationId]);
                        }
                    })();
                };
            }, [presentationId]);

            return (<>
                <EditorSkeleton isInitialized={isInitialized} />
                {isInitialized && <PresentationEditor
                    isSingleSlideEditor={isSingleSlideEditor}
                    allowStyleChange={allowStyleChange}
                    allowSharedSlideEditing={allowSharedSlideEditing}
                    allowSwitchSlide={allowSwitchSlide}
                    allowEditing={allowEditing}
                    onLoaded={onLoaded}
                />}
            </>);
        }
    );

export default PresentationEditorInitializer;

